import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Polyline } from "react-leaflet";
import { fetchRoute } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { OSM_TILE_URL, OSM_ATTRIBUTION, tileClassForTheme } from "@/lib/tiles";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";

const SEGMENTS = [
    { name: "Cork & Kinsale", slug: "waw-cork", covered: true, region: "West Cork" },
    { name: "Kerry Peninsulas", slug: "ring-of-kerry", covered: true, region: "Kerry" },
    { name: "Clare & Cliffs of Moher", slug: "waw-clare", covered: true, region: "Clare" },
    { name: "Galway & Connemara", slug: "waw-connemara-loop", covered: true, region: "Connemara" },
    { name: "Mayo & Achill", slug: "waw-sligo-mayo", covered: true, region: "Mayo" },
    { name: "Sligo & Slieve League", slug: "waw-sligo-mayo", covered: true, region: "Sligo" },
    { name: "Donegal & Inishowen", slug: "waw-donegal", covered: true, region: "Donegal" },
];

export const WawTracker = () => {
    const [polylines, setPolylines] = useState({});
    const [totalKm, setTotalKm] = useState(0);
    const { theme } = useTheme();

    useEffect(() => {
        const uniqueSlugs = [...new Set(SEGMENTS.filter((s) => s.covered && s.slug).map((s) => s.slug))];
        Promise.all(uniqueSlugs.map((slug) => fetchRoute(slug)))
            .then((routes) => {
                const map = {};
                let km = 0;
                routes.forEach((r) => {
                    map[r.slug] = r.polyline;
                    km += r.distance_km;
                });
                setPolylines(map);
                setTotalKm(km);
            })
            .catch(() => {});
    }, []);

    const coveredCount = SEGMENTS.filter((s) => s.covered).length;

    return (
        <section data-testid="waw-tracker" className="py-24 lg:py-32 border-t border-[var(--border-hair)] bg-[var(--surface-alt)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <div className="eyebrow">Progress Tracker</div>
                        <h2 className="mt-3 font-display font-black uppercase text-4xl md:text-6xl leading-none tracking-tight text-[var(--fg)]">
                            The Wild Atlantic Way,<br />segment by segment.
                        </h2>
                        <p className="mt-6 font-body text-[var(--fg-muted)] text-base md:text-lg max-w-xl">
                            Seven signature segments make up the 2,500 km ribbon. Live orange = ridden, mapped and published.
                        </p>
                    </div>
                    <div className="flex items-baseline gap-6">
                        <div>
                            <div className="font-display font-black text-5xl md:text-6xl text-[#E87722] leading-none">{coveredCount}<span className="text-[var(--fg)]">/{SEGMENTS.length}</span></div>
                            <div className="mt-2 text-xs uppercase tracking-[0.24em] text-[var(--fg-muted)] font-body">Segments live</div>
                        </div>
                        <div>
                            <div className="font-display font-black text-5xl md:text-6xl text-[var(--fg)] leading-none">{totalKm.toLocaleString()}</div>
                            <div className="mt-2 text-xs uppercase tracking-[0.24em] text-[var(--fg-muted)] font-body">km mapped</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 relative border border-[var(--border-hair)] overflow-hidden" style={{ height: "560px" }}>
                        <MapContainer
                            center={[53.5, -8.5]}
                            zoom={7}
                            scrollWheelZoom={false}
                            style={{ height: "100%", width: "100%" }}
                            className={`z-0 ${tileClassForTheme(theme)}`}
                        >
                            <TileLayer
                                url={OSM_TILE_URL}
                                attribution={OSM_ATTRIBUTION}
                            />
                            {Object.entries(polylines).map(([slug, path]) => (
                                <Polyline key={slug} positions={path} pathOptions={{ color: "#E87722", weight: 3.5, opacity: 0.95 }} />
                            ))}
                        </MapContainer>
                    </div>

                    <div className="border border-[var(--border-hair)] bg-[var(--card-bg)] p-6 divide-y divide-[var(--border-hair)]">
                        {SEGMENTS.map((s, i) => {
                            const inner = (
                                <div className={`flex items-center gap-3 py-3 ${s.covered ? "text-[var(--fg)]" : "text-[var(--fg-dim)]"} ${s.covered && s.slug ? "group-hover:text-[#E87722]" : ""} transition-colors`}>
                                    {s.covered ? (
                                        <CheckCircle2 size={18} className="text-[#E87722] flex-shrink-0" strokeWidth={2} />
                                    ) : (
                                        <Circle size={18} className="text-neutral-600 flex-shrink-0" strokeWidth={1.5} />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase text-sm tracking-[0.1em] truncate">{s.name}</div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-dim)] mt-0.5">{s.covered ? "Ridden & Mapped" : "Coming soon"}</div>
                                    </div>
                                    {s.covered && s.slug && (
                                        <ArrowRight size={14} className="text-[var(--fg-dim)] group-hover:text-[#E87722] group-hover:translate-x-1 transition-all" />
                                    )}
                                </div>
                            );
                            return s.covered && s.slug ? (
                                <Link key={i} to={`/routes/${s.slug}`} data-testid={`waw-segment-${s.slug}-${i}`} className="group block first:pt-0 last:pb-0">{inner}</Link>
                            ) : (
                                <div key={i} data-testid={`waw-segment-pending-${i}`} className="first:pt-0 last:pb-0">{inner}</div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
